import { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Plus, Edit2, Trash2, Star, StarOff, Upload, Image, Check, Package } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FINISHES = ['Gloss', 'Satin', 'Matte', 'Chrome', 'Brushed', 'Carbon Fiber'];

const WrapsManagement = () => {
  const { studio } = useOutletContext();
  const { getAuthHeader } = useAuth();
  const [wraps, setWraps] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [selectedManufacturers, setSelectedManufacturers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWrap, setEditingWrap] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [savingManufacturers, setSavingManufacturers] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    image_url: '',
    finish: 'Gloss',
    category: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [wrapsRes, mfrsRes, selectedRes] = await Promise.all([
        axios.get(`${API}/wraps`, { headers: getAuthHeader() }),
        axios.get(`${API}/wraps/manufacturers`),
        axios.get(`${API}/wraps/selected-manufacturers`, { headers: getAuthHeader() })
      ]);
      setWraps(wrapsRes.data);
      setManufacturers(mfrsRes.data.manufacturers);
      setSelectedManufacturers(selectedRes.data.selected_manufacturers || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleManufacturerToggle = async (manufacturerName) => {
    const newSelected = selectedManufacturers.includes(manufacturerName)
      ? selectedManufacturers.filter(m => m !== manufacturerName)
      : [...selectedManufacturers, manufacturerName];
    
    setSavingManufacturers(true);
    try {
      await axios.put(`${API}/wraps/selected-manufacturers`, 
        { selected_manufacturers: newSelected },
        { headers: getAuthHeader() }
      );
      setSelectedManufacturers(newSelected);
      // Refresh wraps to get updated list
      const wrapsRes = await axios.get(`${API}/wraps`, { headers: getAuthHeader() });
      setWraps(wrapsRes.data);
      toast.success(selectedManufacturers.includes(manufacturerName) 
        ? `Removed ${manufacturerName} wraps` 
        : `Added ${manufacturerName} wraps`
      );
    } catch (error) {
      toast.error('Failed to update manufacturers');
    } finally {
      setSavingManufacturers(false);
    }
  };

  const handleOpenDialog = (wrap = null) => {
    if (wrap && wrap.is_custom) {
      setEditingWrap(wrap);
      setFormData({
        name: wrap.name,
        image_url: wrap.image_url || '',
        finish: wrap.finish || 'Gloss',
        category: wrap.category || ''
      });
      setImagePreview(wrap.image_url);
    } else {
      setEditingWrap(null);
      setFormData({
        name: '',
        image_url: '',
        finish: 'Gloss',
        category: ''
      });
      setImagePreview(null);
    }
    setDialogOpen(true);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target.result;
      setFormData({ ...formData, image_url: base64 });
      setImagePreview(base64);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!formData.name) {
      toast.error('Please enter a wrap name');
      return;
    }
    if (!formData.image_url) {
      toast.error('Please upload a wrap image');
      return;
    }

    try {
      if (editingWrap) {
        await axios.put(`${API}/wraps/${editingWrap.id}`, formData, { headers: getAuthHeader() });
        toast.success('Wrap updated');
      } else {
        await axios.post(`${API}/wraps`, formData, { headers: getAuthHeader() });
        toast.success('Custom wrap added');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save wrap');
    }
  };

  const handleDelete = async (wrapId) => {
    if (!confirm('Are you sure you want to delete this custom wrap?')) return;

    try {
      await axios.delete(`${API}/wraps/${wrapId}`, { headers: getAuthHeader() });
      toast.success('Wrap deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete wrap');
    }
  };

  const handleToggleFeatured = async (wrapId) => {
    try {
      await axios.put(`${API}/wraps/${wrapId}/feature`, {}, { headers: getAuthHeader() });
      fetchData();
    } catch (error) {
      toast.error('Failed to update wrap');
    }
  };

  // Separate wraps by type
  const preloadedWraps = wraps.filter(w => !w.is_custom);
  const customWraps = wraps.filter(w => w.is_custom);

  // Group preloaded wraps by manufacturer
  const wrapsByManufacturer = preloadedWraps.reduce((acc, wrap) => {
    const mfr = wrap.manufacturer || 'Other';
    if (!acc[mfr]) acc[mfr] = [];
    acc[mfr].push(wrap);
    return acc;
  }, {});

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="wraps-management"
    >
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">Wraps</h1>
          <p className="text-white/60">Select manufacturers and add custom wraps</p>
        </div>
        <Button 
          onClick={() => handleOpenDialog()}
          className="bg-[#FFFFFF] text-black hover:bg-[#E0E0E0]"
          data-testid="add-wrap-btn"
        >
          <Plus className="mr-2 w-4 h-4" /> Add Custom Wrap
        </Button>
      </div>

      {/* Manufacturer Selection */}
      <div className="bg-[#0F0F0F] border border-white/10 rounded-xl p-6 mb-8">
        <div className="flex items-center gap-2 mb-4">
          <Package className="w-5 h-5 text-white/60" />
          <h2 className="text-lg font-semibold">Select Manufacturers You Carry</h2>
        </div>
        <p className="text-white/50 text-sm mb-6">
          Check the manufacturers whose wraps you offer. Their entire catalog will be automatically added to your visualizer.
        </p>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {manufacturers.map((mfr) => {
            const isSelected = selectedManufacturers.includes(mfr.name);
            return (
              <div
                key={mfr.name}
                onClick={() => !savingManufacturers && handleManufacturerToggle(mfr.name)}
                className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  isSelected 
                    ? 'border-white bg-white/10' 
                    : 'border-white/10 hover:border-white/30'
                }`}
              >
                {/* Checkbox indicator */}
                <div className={`absolute top-3 right-3 w-6 h-6 rounded-md flex items-center justify-center ${
                  isSelected ? 'bg-white' : 'border-2 border-white/30'
                }`}>
                  {isSelected && <Check className="w-4 h-4 text-black" />}
                </div>
                
                <h3 className="font-semibold text-lg mb-1 pr-8">{mfr.name}</h3>
                <p className="text-white/50 text-sm">{mfr.wrap_count} wraps available</p>
              </div>
            );
          })}
        </div>
        
        {savingManufacturers && (
          <p className="text-white/50 text-sm mt-4 flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Updating...
          </p>
        )}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-[#0F0F0F] border border-white/10 rounded-xl p-4">
          <p className="text-white/50 text-sm">From Manufacturers</p>
          <p className="text-2xl font-bold">{preloadedWraps.length}</p>
        </div>
        <div className="bg-[#0F0F0F] border border-white/10 rounded-xl p-4">
          <p className="text-white/50 text-sm">Custom Wraps</p>
          <p className="text-2xl font-bold">{customWraps.length}</p>
        </div>
        <div className="bg-[#0F0F0F] border border-white/10 rounded-xl p-4">
          <p className="text-white/50 text-sm">Total in Visualizer</p>
          <p className="text-2xl font-bold">{wraps.length}</p>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          {[...Array(12)].map((_, i) => (
            <div key={i} className="aspect-square bg-[#0F0F0F] rounded-lg animate-pulse"></div>
          ))}
        </div>
      ) : wraps.length === 0 ? (
        <div className="text-center py-16 bg-[#0F0F0F] border border-white/10 rounded-lg">
          <Package className="w-12 h-12 mx-auto mb-4 text-white/20" />
          <p className="text-white/40 mb-2">No wraps in your visualizer yet</p>
          <p className="text-white/30 text-sm mb-6">Select manufacturers above or add custom wraps</p>
        </div>
      ) : (
        <div className="space-y-10">
          {/* Pre-loaded wraps by manufacturer */}
          {Object.entries(wrapsByManufacturer).map(([manufacturer, manufacturerWraps]) => (
            <div key={manufacturer}>
              <div className="flex items-center gap-2 mb-4">
                <h2 className="text-lg font-semibold text-white/80">{manufacturer}</h2>
                <span className="text-white/40 text-sm">({manufacturerWraps.length} wraps)</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
                {manufacturerWraps.map((wrap) => (
                  <div 
                    key={wrap.id}
                    className="group relative aspect-square bg-[#0F0F0F] border border-white/10 rounded-lg overflow-hidden"
                  >
                    {wrap.image_url ? (
                      <img 
                        src={wrap.image_url} 
                        alt={wrap.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-900" />
                    )}
                    
                    {/* Info */}
                    <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/90 to-transparent">
                      <p className="font-medium truncate text-xs">{wrap.name}</p>
                      <p className="text-xs text-white/50">{wrap.finish}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Custom wraps */}
          {customWraps.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <h2 className="text-lg font-semibold text-white/80">Custom Wraps</h2>
                <span className="text-white/40 text-sm">({customWraps.length} wraps)</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
                {customWraps.map((wrap) => (
                  <div 
                    key={wrap.id}
                    className="group relative aspect-square bg-[#0F0F0F] border border-white/10 rounded-lg overflow-hidden"
                  >
                    {wrap.image_url ? (
                      <img 
                        src={wrap.image_url} 
                        alt={wrap.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-900" />
                    )}
                    
                    {/* Custom badge */}
                    <div className="absolute top-2 left-2 bg-blue-500 text-white text-xs px-2 py-0.5 rounded font-medium">
                      Custom
                    </div>

                    {/* Overlay with actions */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button
                        onClick={() => handleOpenDialog(wrap)}
                        className="p-2 bg-white/10 rounded-lg hover:bg-white/20"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(wrap.id)}
                        className="p-2 bg-white/10 rounded-lg hover:bg-red-500/50"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>

                    {/* Info */}
                    <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/90 to-transparent">
                      <p className="font-medium truncate text-xs">{wrap.name}</p>
                      <p className="text-xs text-white/50">{wrap.finish}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Custom Wrap Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0F0F0F] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingWrap ? 'Edit Custom Wrap' : 'Add Custom Wrap'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-5 py-4">
            {/* Image Upload */}
            <div>
              <Label className="text-white/70 mb-2 block">Wrap Image *</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              
              {imagePreview ? (
                <div className="relative">
                  <img 
                    src={imagePreview} 
                    alt="Wrap preview" 
                    className="w-full h-40 object-cover rounded-lg border border-white/10"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 hover:opacity-100 transition-opacity rounded-lg"
                  >
                    <span className="text-sm text-white">Change Image</span>
                  </button>
                </div>
              ) : (
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full h-40 border-2 border-dashed border-white/20 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-white/40 transition-colors"
                >
                  <Upload className="w-8 h-8 text-white/30 mb-2" />
                  <span className="text-sm text-white/50">Click to upload wrap image</span>
                  <span className="text-xs text-white/30 mt-1">Hero image or texture swatch</span>
                </div>
              )}
            </div>

            {/* Name */}
            <div>
              <Label className="text-white/70 mb-2 block">Name *</Label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Custom Chameleon Purple"
                className="bg-[#171717] border-white/10"
              />
            </div>

            {/* Finish */}
            <div>
              <Label className="text-white/70 mb-2 block">Finish</Label>
              <select
                value={formData.finish}
                onChange={(e) => setFormData({ ...formData, finish: e.target.value })}
                className="w-full bg-[#171717] border border-white/10 rounded-lg h-10 px-3 text-white"
              >
                {FINISHES.map((finish) => (
                  <option key={finish} value={finish}>{finish}</option>
                ))}
              </select>
            </div>

            {/* Category */}
            <div>
              <Label className="text-white/70 mb-2 block">Category</Label>
              <Input
                type="text"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder="e.g., Special Effects, Color Shift"
                className="bg-[#171717] border-white/10"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleSave}
              className="bg-[#FFFFFF] text-black hover:bg-[#E0E0E0]"
            >
              {editingWrap ? 'Save Changes' : 'Add Wrap'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default WrapsManagement;
